import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getTrip } from '@/services/tripService'
import { listDays, addNextDay, deleteDay } from '@/services/dayService'
import {
  listActivitiesByDay,
  listUnscheduledActivities,
  createActivity,
  assignActivityToDay,
  updateActivity,
  deleteActivity,
  type Activity,
} from '@/services/activityService'
import { ActivityForm } from '@/components/ActivityForm'
import type { NewActivityFormValues, NewActivityFormInput } from '@/utils/schemas'

const CATEGORY_COLOR: Record<string, string> = {
  transport: '#c97f4a',
  accommodation: '#6e9aa8',
  discovery: '#c9a15a',
  meal: '#9a8fb0',
}

function activityToFormInput(activity: Activity): NewActivityFormInput {
  return {
    title: activity.title,
    category: (activity.category ?? '') as NewActivityFormInput['category'],
    location: activity.location ?? '',
    latitude: activity.latitude !== null ? String(activity.latitude) : '',
    longitude: activity.longitude !== null ? String(activity.longitude) : '',
    startTime: activity.start_time?.slice(0, 5) ?? '',
    endTime: activity.end_time?.slice(0, 5) ?? '',
    note: activity.note ?? '',
  }
}

function formatDay(date: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`))
}

export function ItineraryPage() {
  const { t } = useTranslation()
  const { tripId } = useParams<{ tripId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedDayId, setSelectedDayId] = useState<string | null>(null)
  const [showUnscheduled, setShowUnscheduled] = useState(false)
  const [showActivityForm, setShowActivityForm] = useState(false)
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [confirmDeleteDay, setConfirmDeleteDay] = useState(false)

  const tripQuery = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => getTrip(tripId!),
    enabled: !!tripId,
  })

  const daysQuery = useQuery({
    queryKey: ['days', tripId],
    queryFn: () => listDays(tripId!),
    enabled: !!tripId,
  })

  const days = daysQuery.data ?? []
  const activeDayId = showUnscheduled ? null : (selectedDayId ?? days[0]?.id ?? null)
  const canDeleteActiveDay =
    days.length > 1 &&
    !!activeDayId &&
    (activeDayId === days[0]?.id || activeDayId === days.at(-1)?.id)

  const activitiesQuery = useQuery({
    queryKey: showUnscheduled ? ['activities', 'unscheduled', tripId] : ['activities', activeDayId],
    queryFn: () =>
      showUnscheduled ? listUnscheduledActivities(tripId!) : listActivitiesByDay(activeDayId!),
    enabled: showUnscheduled ? !!tripId : !!activeDayId,
  })

  const addDayMutation = useMutation({
    mutationFn: () => addNextDay(tripId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['days', tripId] })
    },
  })

  const deleteDayMutation = useMutation({
    mutationFn: (dayId: string) => deleteDay(tripId!, dayId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['days', tripId] })
      await queryClient.invalidateQueries({ queryKey: ['activities'] })
      setSelectedDayId(null)
      setConfirmDeleteDay(false)
    },
  })

  const assignDayMutation = useMutation({
    mutationFn: ({ activityId, dayId }: { activityId: string; dayId: string }) =>
      assignActivityToDay(activityId, dayId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['activities'] })
    },
  })

  const addActivityMutation = useMutation({
    mutationFn: (values: NewActivityFormValues) =>
      createActivity({
        tripId: tripId!,
        dayId: showUnscheduled ? undefined : activeDayId!,
        ...values,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['activities'] })
      setShowActivityForm(false)
    },
  })

  const updateActivityMutation = useMutation({
    mutationFn: ({ activityId, values }: { activityId: string; values: NewActivityFormValues }) =>
      updateActivity(activityId, values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['activities'] })
      setEditingActivityId(null)
    },
  })

  const deleteActivityMutation = useMutation({
    mutationFn: (activityId: string) => deleteActivity(activityId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['activities'] })
      setConfirmDeleteId(null)
    },
  })

  if (tripQuery.isLoading || daysQuery.isLoading) {
    return <p className="bg-night p-10 text-mist">{t('itinerary.loading')}</p>
  }

  const trip = tripQuery.data

  return (
    <div className="min-h-screen bg-night">
      <div
        className="border-b border-line px-4 py-5 md:px-10 md:py-7"
        style={{ background: 'linear-gradient(160deg, var(--color-panel), var(--color-night))' }}
      >
        <button
          type="button"
          onClick={() => navigate('/trips')}
          className="mb-3.5 font-sans text-sm text-mist"
        >
          {t('itinerary.backToTrips')}
        </button>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl text-ivory">{trip?.title}</h1>
            <div className="mt-1.5 font-mono text-xs text-brass-dim">
              {trip?.destination} — {trip?.start_date} → {trip?.end_date}
            </div>
          </div>
          <div className="-mx-4 flex w-full gap-2.5 overflow-x-auto px-4 md:mx-0 md:w-auto md:px-0">
            <button
              type="button"
              onClick={() => navigate(`/trips/${tripId}/edit`)}
              className="shrink-0 rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory hover:border-brass hover:text-brass"
            >
              {t('itinerary.editTrip')}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/trips/${tripId}/members`)}
              className="shrink-0 rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory hover:border-brass hover:text-brass"
            >
              {t('itinerary.members')}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/trips/${tripId}/bookings`)}
              className="shrink-0 rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory hover:border-brass hover:text-brass"
            >
              {t('itinerary.bookings')}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/trips/${tripId}/map`)}
              className="shrink-0 rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory hover:border-brass hover:text-brass"
            >
              {t('itinerary.map')}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/trips/${tripId}/suggestions`)}
              className="shrink-0 rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory hover:border-brass hover:text-brass"
            >
              {t('itinerary.suggestions')}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/trips/${tripId}/proposals`)}
              className="shrink-0 rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory hover:border-brass hover:text-brass"
            >
              {t('itinerary.proposals')}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/trips/${tripId}/checklist`)}
              className="shrink-0 rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory hover:border-brass hover:text-brass"
            >
              {t('itinerary.checklist')}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/trips/${tripId}/documents`)}
              className="shrink-0 rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory hover:border-brass hover:text-brass"
            >
              {t('itinerary.documents')}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/trips/${tripId}/budget`)}
              className="shrink-0 rounded border border-line-strong px-4 py-2 font-sans text-sm text-ivory hover:border-brass hover:text-brass"
            >
              {t('itinerary.budget')}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr]">
        <div className="border-b border-line py-2 md:border-r md:border-b-0 md:py-4">
          <div className="flex overflow-x-auto md:block md:overflow-visible">
            {days.map((day) => (
              <button
                key={day.id}
                type="button"
                onClick={() => {
                  setShowUnscheduled(false)
                  setSelectedDayId(day.id)
                }}
                className={
                  'shrink-0 border-l-2 px-5 py-3 text-left md:w-full ' +
                  (!showUnscheduled && day.id === activeDayId
                    ? 'border-l-brass bg-panel'
                    : 'border-l-transparent bg-transparent')
                }
              >
                <div
                  className={
                    'font-sans text-[13.5px] font-medium ' +
                    (!showUnscheduled && day.id === activeDayId ? 'text-ivory' : 'text-mist')
                  }
                >
                  {formatDay(day.date)}
                </div>
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowUnscheduled(true)}
              className={
                'shrink-0 border-l-2 px-5 py-3 text-left md:w-full ' +
                (showUnscheduled
                  ? 'border-l-brass bg-panel'
                  : 'border-l-transparent bg-transparent')
              }
            >
              <div
                className={
                  'font-sans text-[13.5px] font-medium ' +
                  (showUnscheduled ? 'text-ivory' : 'text-mist')
                }
              >
                {t('itinerary.unscheduled')}
              </div>
            </button>
          </div>
          <div className="px-5 py-2.5">
            <button
              type="button"
              onClick={() => addDayMutation.mutate()}
              disabled={addDayMutation.isPending}
              className="font-sans text-xs text-mist hover:text-brass"
            >
              {t('itinerary.addDay')}
            </button>
          </div>
        </div>

        <div className="px-4 py-6 md:px-10">
          {canDeleteActiveDay && (
            <div className="mb-4 flex justify-end">
              {confirmDeleteDay ? (
                <div className="rounded-r border-l-[3px] border-line border-l-brass-warm bg-panel px-4 py-3">
                  {deleteDayMutation.isError && (
                    <p role="alert" className="mb-2 text-sm text-brass-warm">
                      {t('itinerary.deleteDayError')}
                    </p>
                  )}
                  <p className="mb-2 text-sm text-ivory">{t('itinerary.confirmDeleteDay')}</p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteDay(false)}
                      className="rounded border border-line-strong px-3 py-1.5 font-sans text-xs text-ivory"
                    >
                      {t('itinerary.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteDayMutation.mutate(activeDayId!)}
                      disabled={deleteDayMutation.isPending}
                      className="rounded border border-brass-warm px-3 py-1.5 font-sans text-xs font-medium text-brass-warm disabled:opacity-50"
                    >
                      {t('itinerary.confirmDeleteDayButton')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDeleteDay(true)}
                  className="font-sans text-xs text-mist hover:text-brass-warm"
                >
                  {t('itinerary.deleteDay')}
                </button>
              )}
            </div>
          )}

          {activitiesQuery.data?.map((activity) => (
            <div key={activity.id} className="mb-3.5 grid grid-cols-[44px_1fr] gap-4">
              <div className="pt-4 text-right font-mono text-xs text-brass-dim">
                {activity.start_time?.slice(0, 5)}
              </div>
              {editingActivityId === activity.id ? (
                <ActivityForm
                  defaultValues={activityToFormInput(activity)}
                  isPending={updateActivityMutation.isPending}
                  isError={updateActivityMutation.isError}
                  submitLabel={t('itinerary.save')}
                  onCancel={() => setEditingActivityId(null)}
                  onSubmit={(values) =>
                    updateActivityMutation.mutate({ activityId: activity.id, values })
                  }
                />
              ) : (
                <div className="rounded-md border border-line bg-panel px-4 py-3.5">
                  <div className="flex items-center justify-between gap-2.5">
                    <span className="flex items-center gap-2 font-sans text-[15px] font-medium text-ivory">
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full"
                        style={{ background: CATEGORY_COLOR[activity.category ?? ''] }}
                      />
                      {activity.title}
                    </span>
                    {activity.category && (
                      <span
                        className="font-mono text-[10px] tracking-wide uppercase"
                        style={{ color: CATEGORY_COLOR[activity.category] }}
                      >
                        {t(`itinerary.category.${activity.category}`)}
                      </span>
                    )}
                  </div>
                  {activity.location && (
                    <div className="mt-1 text-sm text-mist">{activity.location}</div>
                  )}
                  {activity.note && <div className="mt-1 text-sm text-mist">{activity.note}</div>}
                  {showUnscheduled && (
                    <select
                      aria-label={t('itinerary.assignToDay')}
                      className="mt-2.5 rounded border border-line-strong bg-panel-2 px-2 py-1 font-sans text-xs text-ivory"
                      value=""
                      disabled={assignDayMutation.isPending}
                      onChange={(e) => {
                        if (e.target.value) {
                          assignDayMutation.mutate({
                            activityId: activity.id,
                            dayId: e.target.value,
                          })
                        }
                      }}
                    >
                      <option value="" disabled>
                        {t('itinerary.assignToDay')}
                      </option>
                      {days.map((day) => (
                        <option key={day.id} value={day.id}>
                          {formatDay(day.date)}
                        </option>
                      ))}
                    </select>
                  )}

                  {confirmDeleteId === activity.id ? (
                    <div className="mt-2.5 rounded-r border-l-[3px] border-line border-l-brass-warm bg-panel-2 px-3 py-2.5">
                      {deleteActivityMutation.isError && (
                        <p role="alert" className="mb-2 text-sm text-brass-warm">
                          {t('itinerary.deleteError')}
                        </p>
                      )}
                      <p className="mb-2 text-sm text-ivory">
                        {t('itinerary.confirmDeleteActivity')}
                      </p>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(null)}
                          className="rounded border border-line-strong px-3 py-1.5 font-sans text-xs text-ivory"
                        >
                          {t('itinerary.cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteActivityMutation.mutate(activity.id)}
                          disabled={deleteActivityMutation.isPending}
                          className="rounded border border-brass-warm px-3 py-1.5 font-sans text-xs font-medium text-brass-warm disabled:opacity-50"
                        >
                          {t('itinerary.confirmDeleteActivityButton')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2.5 flex gap-4">
                      <button
                        type="button"
                        onClick={() => setEditingActivityId(activity.id)}
                        className="font-sans text-xs text-mist hover:text-brass"
                      >
                        {t('itinerary.editActivity')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(activity.id)}
                        className="font-sans text-xs text-mist hover:text-brass-warm"
                      >
                        {t('itinerary.deleteActivity')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {showActivityForm ? (
            <div className="grid grid-cols-[44px_1fr] gap-4">
              <div />
              <ActivityForm
                isPending={addActivityMutation.isPending}
                isError={addActivityMutation.isError}
                submitLabel={t('itinerary.save')}
                onCancel={() => setShowActivityForm(false)}
                onSubmit={(values) => addActivityMutation.mutate(values)}
              />
            </div>
          ) : (
            <div className="grid grid-cols-[44px_1fr] gap-4">
              <div />
              <button
                type="button"
                onClick={() => setShowActivityForm(true)}
                className="rounded-md border border-dashed border-line-strong px-4 py-3 text-left font-sans text-sm text-mist hover:border-brass hover:text-brass"
              >
                {t('itinerary.addActivity')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
